// The decisions behind reading a practice line aloud, kept away from the DOM
// and from `window`.
//
// Why separate: the browser's speechSynthesis does not exist in the node
// environment the project's vitest runs in, and the project has no jsdom. Put
// the choosing here and it is testable; leave it inline in the page entry and
// the only way to check "does the slow pass actually run slower" is to hold a
// phone. app/javascript/pages/speak-topic.js does the wiring.

// The browser default. Naming it keeps the two speeds comparable in one place.
export const NORMAL_RATE = 1;

// ⚠️ Slower than this and the synthesiser starts inserting pauses inside a
// word, which teaches the wrong rhythm. This is the floor that still sounds
// like a sentence.
export const SLOW_RATE = 0.6;

// A BCP 47 tag for English is "en", or "en" followed by a subtag separator.
// ⛔ Not a prefix match on "en": "eng-XX" and "enz" are other languages.
const ENGLISH_TAG = /^en(?:[-_]|$)/i;

/**
 * The English voices among whatever the browser reported.
 *
 * @param {Array<SpeechSynthesisVoice>} [voices] the browser's voice list
 * @returns {Array<SpeechSynthesisVoice>} the English ones, in the reported order
 */
export function englishVoices(voices) {
  if (!Array.isArray(voices)) return [];

  return voices.filter((voice) => typeof voice?.lang === 'string' && ENGLISH_TAG.test(voice.lang));
}

/**
 * Whether the playback controls have earned the right to be visible.
 *
 * A control that is visible and does nothing is worse than no control: this
 * reader already believes the problem is them. So this answers false for a
 * browser with no speech synthesis, for a device with no English voice, and
 * for the empty list iOS returns on the first call — and the page asks again
 * when the browser says the list has changed.
 *
 * @param {Array<SpeechSynthesisVoice>} [voices] the browser's voice list, or nothing
 * @returns {boolean} true when the device can actually speak an English line
 */
export function shouldRevealControls(voices) {
  return englishVoices(voices).length > 0;
}

// ⛔ macOS ships these as English voices, and Chrome lists them in alphabetical
// order, so "Albert" (a croak) and "Bad News" (a dirge) came first and were
// what a Mac reader heard. They are sound effects, not reading voices: never
// offer them for practice.
const NOVELTY_VOICES = new Set([
  'albert', 'bad news', 'bahh', 'bells', 'boing', 'bubbles', 'cellos',
  'deranged', 'good news', 'hysterical', 'jester', 'organ', 'pipe organ',
  'princess', 'superstar', 'trinoids', 'whisper', 'wobble', 'zarvox'
]);

// Older engines that still read a sentence, but flat and buzzy. Offered, never
// picked by default while anything better is on the device.
const ROBOTIC_VOICES = new Set([
  'fred', 'junior', 'ralph', 'kathy', 'agnes', 'bruce', 'vicki', 'victoria',
  // The "Eloquence" set macOS added alongside the Siri voices.
  'eddy', 'flo', 'grandma', 'grandpa', 'reed', 'rocko', 'sandy', 'shelley'
]);

// The browser does not report a voice's gender, so it comes off the names the
// big platforms ship. A name not listed gets no gender rather than a guess.
const FEMALE_NAMES = new Set([
  'samantha', 'karen', 'moira', 'tessa', 'veena', 'fiona', 'serena', 'kate',
  'martha', 'allison', 'ava', 'susan', 'zoe', 'joelle', 'noelle', 'catherine',
  'nicky', 'kathy', 'victoria', 'agnes', 'vicki', 'flo', 'grandma', 'sandy',
  'shelley', 'zira', 'aria', 'jenny', 'ana', 'michelle', 'sonia', 'libby',
  'maisie', 'natasha', 'clara', 'emily', 'neerja', 'molly', 'luna', 'leah',
  'hazel', 'heera', 'linda', 'emma', 'amber', 'ashley', 'cora',
  'elizabeth', 'jane', 'nancy', 'sara', 'isla', 'imogen', 'olivia'
]);
const MALE_NAMES = new Set([
  'alex', 'daniel', 'rishi', 'oliver', 'arthur', 'tom', 'evan', 'nathan',
  'lee', 'gordon', 'aaron', 'fred', 'junior', 'ralph', 'bruce', 'eddy',
  'grandpa', 'reed', 'rocko', 'david', 'mark', 'guy', 'christopher', 'eric',
  'roger', 'steffan', 'ryan', 'thomas', 'william', 'liam', 'connor',
  'prabhat', 'mitchell', 'wayne', 'luke', 'george', 'ravi', 'james',
  'richard', 'sean', 'andrew', 'brian', 'davis', 'jason', 'tony', 'elliot'
]);

// The accent is the one thing a learner reliably knows they want, so it leads
// the label. Keys match the i18n keys under speak.topic.voice.accent.
const ACCENTS = { us: 'us', gb: 'gb', au: 'au', ie: 'ie', in: 'in', za: 'za', ca: 'ca', nz: 'nz', sg: 'sg' };

function nameKey(voice) {
  // "Microsoft Aria Online (Natural) - English (United States)" and
  // "Google UK English Female" carry the vendor first; the person's name is
  // the first word after it.
  const name = String(voice?.name || '')
    .replace(/^(microsoft|google|apple)\s+/i, '')
    .replace(/\s*\(.*$/, '')
    .trim()
    .toLowerCase();

  return { full: name, first: name.split(/\s+/)[0] || '' };
}

function isNovelty(voice) {
  return NOVELTY_VOICES.has(nameKey(voice).full);
}

function isRobotic(voice) {
  return ROBOTIC_VOICES.has(nameKey(voice).first);
}

function isNatural(voice) {
  return /natural|neural|premium|enhanced|siri/i.test(String(voice?.name || ''));
}

/**
 * The English voices worth practising with: the novelty sound effects removed.
 *
 * Where a device somehow has nothing else, the full English list comes back —
 * ⛔ a strange voice still beats a line the reader cannot hear at all.
 *
 * @param {Array<SpeechSynthesisVoice>} [voices] the browser's voice list
 * @returns {Array<SpeechSynthesisVoice>}
 */
export function practiceVoices(voices) {
  const english = englishVoices(voices);
  const usable = english.filter((voice) => !isNovelty(voice));

  return usable.length > 0 ? usable : english;
}

function voiceScore(voice) {
  let score = 0;

  if (isNatural(voice)) score += 4;
  if (isRobotic(voice)) score -= 4;
  // The accent most learners here are aiming at, then the other common one.
  if (/^en[-_]us/i.test(voice.lang)) score += 2;
  else if (/^en[-_]gb/i.test(voice.lang)) score += 1;
  // The browser's own pick is usually a reasonable one.
  if (voice.default) score += 1;

  return score;
}

/**
 * The practice voices, best-sounding first.
 *
 * ⛔ A stable sort on a fixed score, so the same device still gives the same
 * role the same voice on every page and a "line 4 sounds wrong" report stays
 * reproducible.
 *
 * @param {Array<SpeechSynthesisVoice>} [voices] the browser's voice list
 * @returns {Array<SpeechSynthesisVoice>}
 */
export function rankedVoices(voices) {
  return practiceVoices(voices)
    .map((voice, index) => ({ voice, index, score: voiceScore(voice) }))
    .sort((a, b) => b.score - a.score || a.index - b.index)
    .map(({ voice }) => voice);
}

/**
 * The voices to offer in the picker: the ranked list without the old robotic
 * engines. A Mac has twenty of those, and a list of thirty-odd lookalike
 * entries is not a choice anyone can make by ear. They stay where the device
 * has fewer than two better ones, so there is still something to pick.
 *
 * ⛔ A voice a side is using right now always stays in the list, robotic or
 * not. Otherwise the select shows a blank while that voice keeps speaking.
 *
 * @param {Array<SpeechSynthesisVoice>} [voices] the browser's voice list
 * @param {Array<?SpeechSynthesisVoice>} [inUse] the voices the two sides use
 * @returns {Array<SpeechSynthesisVoice>}
 */
export function choosableVoices(voices, inUse = []) {
  const ranked = rankedVoices(voices);
  const better = ranked.filter((voice) => !isRobotic(voice));
  if (better.length < 2) return ranked;

  const keep = new Set(inUse.filter(Boolean).map(voiceId));
  return ranked.filter((voice) => !isRobotic(voice) || keep.has(voiceId(voice)));
}

/**
 * The id a chosen voice is saved under. voiceURI where the browser gives one,
 * the name where it does not.
 *
 * @param {?SpeechSynthesisVoice} voice
 * @returns {string}
 */
export function voiceId(voice) {
  return String(voice?.voiceURI || voice?.name || '');
}

/**
 * The voice a saved id points at, if this device still has it.
 *
 * @param {Array<SpeechSynthesisVoice>} [voices]
 * @param {string} [id]
 * @returns {?SpeechSynthesisVoice}
 */
export function findVoice(voices, id) {
  if (!id) return null;

  return practiceVoices(voices).find((voice) => voiceId(voice) === id) || null;
}

/**
 * One voice per side of the counter.
 *
 * The dialogue is a scene between two people and the learner practises both
 * parts, so hearing two voices is the cue that says which half is theirs.
 * Where the device has only one English voice both roles share it — ⛔ never
 * disable a role for this, the reader still needs to hear the line.
 *
 * A voice the reader picked wins. A saved pick the device no longer has (a
 * different browser, a removed voice) falls back to the default quietly.
 *
 * @param {Array<SpeechSynthesisVoice>} [voices] the browser's voice list
 * @param {{other?: string, learner?: string}} [saved] voice ids the reader chose
 * @returns {{other: ?SpeechSynthesisVoice, learner: ?SpeechSynthesisVoice}}
 */
export function assignRoleVoices(voices, saved = {}) {
  const ranked = rankedVoices(voices);
  if (ranked.length === 0) return { other: null, learner: null };

  const other = findVoice(voices, saved?.other) || ranked[0];
  const learner =
    findVoice(voices, saved?.learner) ||
    ranked.find((voice) => voiceId(voice) !== voiceId(other)) ||
    other;

  return { other, learner };
}

/**
 * A voice described the way a learner would describe it: which accent, a
 * man or a woman, and whether it sounds natural. Keys, not words — the page
 * turns them into the reader's language.
 *
 * @param {SpeechSynthesisVoice} voice
 * @returns {{accent: string, gender: ?string, quality: ?string, online: boolean}}
 */
export function describeVoice(voice) {
  const region = String(voice?.lang || '').split(/[-_]/)[1]?.toLowerCase();
  const name = String(voice?.name || '').toLowerCase();
  const { first } = nameKey(voice);

  let gender = null;
  if (/\bfemale\b/.test(name) || FEMALE_NAMES.has(first)) gender = 'female';
  else if (/\bmale\b/.test(name) || MALE_NAMES.has(first)) gender = 'male';
  // Chrome's one American Google voice is a woman and does not say so.
  else if (/^google us english$/.test(name)) gender = 'female';

  let quality = null;
  if (isNatural(voice)) quality = 'natural';
  else if (isRobotic(voice)) quality = 'robotic';

  return {
    accent: ACCENTS[region] || 'other',
    gender,
    quality,
    // A voice that streams from a server goes silent offline, which on a
    // phone on the MRT looks like the button is broken.
    online: voice?.localService === false
  };
}

/**
 * Plain labels for a list of voices, made unique.
 *
 * Two American women on one device would otherwise read the same, so a repeat
 * gets a number rather than the vendor's product name.
 *
 * @param {Array<SpeechSynthesisVoice>} voices
 * @param {(description: object) => string} format turns one description into words
 * @returns {Array<{id: string, label: string}>}
 */
export function voiceOptions(voices, format) {
  const seen = new Map();

  return voices.map((voice) => {
    const base = format(describeVoice(voice));
    const count = (seen.get(base) || 0) + 1;
    seen.set(base, count);

    return { id: voiceId(voice), label: count === 1 ? base : `${base} ${count}` };
  });
}

/**
 * The utterance rate for a named speed.
 *
 * @param {string} [speed] "normal" or "slow"
 * @returns {number} the rate to hand to the utterance
 */
export function rateFor(speed) {
  return speed === 'slow' ? SLOW_RATE : NORMAL_RATE;
}

/**
 * The lines of a dialogue in the order they are spoken.
 *
 * Order comes off the line numbers, not off the order the elements sit in, and
 * a line with no usable number is dropped rather than spoken out of place.
 *
 * @param {Array<{number: number}>} [lines] the lines, in any order
 * @returns {Array<{number: number}>} a new array, ordered by line number
 */
export function playbackOrder(lines) {
  if (!Array.isArray(lines)) return [];

  return lines
    .filter((line) => Number.isFinite(line?.number))
    .sort((a, b) => a.number - b.number);
}
